import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Loader2, Search, Info } from 'lucide-react';
import type { GoogleContact } from '@/types/googleContacts';

interface GoogleContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contacts: GoogleContact[];
  isLoading: boolean;
  onImport: (selectedContacts: GoogleContact[]) => void;
  maxSelectable?: number;
}

const GoogleContactsModal = ({
  isOpen,
  onClose,
  contacts,
  isLoading,
  onImport,
  maxSelectable,
}: GoogleContactsModalProps) => {
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Reset selection and search when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedContacts(new Set());
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleToggleContact = (resourceName: string) => {
    setSelectedContacts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(resourceName)) {
        newSet.delete(resourceName);
      } else {
        // Check if we've reached the max selectable limit
        if (maxSelectable !== undefined && newSet.size >= maxSelectable) {
          return prev; // Don't add more if limit reached
        }
        newSet.add(resourceName);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedContacts.size > 0) {
      // If any contacts are selected, deselect all
      setSelectedContacts(new Set());
    } else {
      // If no contacts are selected, select up to the limit
      const contactsToSelect = maxSelectable !== undefined
        ? filteredContacts.slice(0, maxSelectable)
        : filteredContacts;
      setSelectedContacts(new Set(contactsToSelect.map((c) => c.resourceName)));
    }
  };

  const handleImport = () => {
    const selected = contacts.filter((contact) =>
      selectedContacts.has(contact.resourceName)
    );
    onImport(selected);
  };

  const getContactName = (contact: GoogleContact) => {
    if (contact.names && contact.names[0]) {
      return contact.names[0].displayName || 'Unnamed Contact';
    }
    return 'Unnamed Contact';
  };

  // Filter contacts based on search query
  const filteredContacts = useMemo(() => {
    if (!searchQuery.trim()) return contacts;
    
    const query = searchQuery.toLowerCase();
    return contacts.filter((contact) => {
      const name = getContactName(contact).toLowerCase();
      return name.includes(query);
    });
  }, [contacts, searchQuery]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[621px] p-0 rounded-[8px] border-0">
        <div className="p-8 md:p-[56px]">
          {/* Header */}
          <div className="mb-8">
            <p className="text-[#6C4F77]/50 text-sm font-normal mb-2">
              Step 2 of 3 • Add contacts
            </p>
            <h1 className="text-[20px] md:text-[32px] font-bold text-[#6C4F77] mb-2 font-modernist">
              Select your Google Contacts
            </h1>
            <p className="text-[#666666] text-[14px] md:text-[16px] font-normal">
              Choose who you want to remember with WabiSabi. We'll only save the people you pick.
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#6C4F77]" />
              <span className="ml-2 text-gray-600">Loading your contacts...</span>
            </div>
          ) : (
            <>
              {/* Search Field */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search contacts"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 border-gray-200 focus:border-[#6C4F77] focus:ring-[#6C4F77]"
                />
              </div>

              {/* Select All */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  {selectedContacts.size}/{maxSelectable || 4} contacts selected
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  className="text-[#6C4F77] hover:text-[#5A3A68] hover:bg-[#6C4F77]/10"
                >
                  {selectedContacts.size > 0 ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              {/* Contacts List */}
              <ScrollArea className="h-[300px] pr-4 google-contacts-scrollbar">
                <div className="space-y-2">
                  {filteredContacts.map((contact) => {
                    const name = getContactName(contact);
                    const isSelected = selectedContacts.has(contact.resourceName);
                    const isLimitReached = maxSelectable !== undefined && selectedContacts.size >= maxSelectable;
                    const isDisabled = !isSelected && isLimitReached;

                    return (
                      <div
                        key={contact.resourceName}
                        className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer ${
                          isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => !isDisabled && handleToggleContact(contact.resourceName)}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          onCheckedChange={() => !isDisabled && handleToggleContact(contact.resourceName)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded-[4px] border-gray-300 data-[state=checked]:bg-[#6C4F77] data-[state=checked]:border-[#6C4F77]"
                        />
                        <p className="text-gray-900">{name}</p>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-8">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1 border-gray-200 text-gray-600 font-normal text-[14px] font-modernist py-[20px]"
                >
                  Back
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={selectedContacts.size === 0}
                  className="flex-1 bg-[#6C4F77] hover:bg-[#5A3A68] disabled:bg-gray-300 text-white font-bold text-[14px] font-modernist py-[20px]"
                >
                  Import Selected
                </Button>
              </div>

              {/* Privacy Note */}
              <p className="text-center text-[12px] text-gray-500 mt-4">
                You can change or remove contacts anytime. Your privacy is always respected.
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GoogleContactsModal;